import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { endOfMonth, subMonths, isValid, isAfter, parseISO, startOfDay } from "date-fns";
import { useFeatureFlag } from "@jsq/flags-react";

export const getDefaultAsOfDate = (): Date => {
  return endOfMonth(subMonths(new Date(), 1));
};

const clampToMaxDate = (date: Date, maxDate: Date): Date => {
  return isAfter(startOfDay(date), startOfDay(maxDate)) ? maxDate : date;
};

const getInitialAsOfDate = (maxDate: Date): Date => {
  const params = new URLSearchParams(window.location.search);
  const asOfDateParam = params.get("asOfDate");
  let initial = getDefaultAsOfDate();
  if (asOfDateParam) {
    const parsed = parseISO(asOfDateParam);
    if (isValid(parsed)) {
      initial = parsed;
    }
  }
  return clampToMaxDate(initial, maxDate);
};

export const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface BillingDateContextType {
  asOfDate: Date;
  setAsOfDate: (date: Date) => void;
  asOfDateString: string;
  maxAsOfDate: Date;
}

const BillingDateContext = createContext<BillingDateContextType | undefined>(undefined);

interface BillingDateProviderProps {
  children: ReactNode;
}

export const BillingDateProvider: React.FC<BillingDateProviderProps> = ({ children }) => {
  const isAsOfDateTodaySelectable = useFeatureFlag("billingAsOfDateTodaySelectable", false);

  const maxAsOfDate = useMemo(() => {
    const maxDate = new Date();
    if (!isAsOfDateTodaySelectable) {
      maxDate.setDate(maxDate.getDate() - 1);
    }
    return maxDate;
  }, [isAsOfDateTodaySelectable]);

  const [asOfDate, setAsOfDateInternal] = useState<Date>(() => getInitialAsOfDate(maxAsOfDate));

  const setAsOfDate = useCallback(
    (date: Date) => {
      setAsOfDateInternal(clampToMaxDate(date, maxAsOfDate));
    },
    [maxAsOfDate],
  );

  useEffect(() => {
    setAsOfDateInternal((current) => clampToMaxDate(current, maxAsOfDate));
  }, [maxAsOfDate]);

  const value = {
    asOfDate,
    setAsOfDate,
    asOfDateString: formatDateForApi(asOfDate),
    maxAsOfDate,
  };

  return <BillingDateContext.Provider value={value}>{children}</BillingDateContext.Provider>;
};

export const useBillingDate = (): BillingDateContextType => {
  const context = useContext(BillingDateContext);
  if (context === undefined) {
    throw new Error("useBillingDate must be used within a BillingDateProvider");
  }
  return context;
};
